import { doc, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase";
import { VALID_CATEGORIES, VALID_BOOKING_STATUSES } from "../components/constants";
import { stripUndefined } from "./helpers";
import { getCostRange } from "./costHelpers";
import { getSetting } from "./settings";
import { authenticatedFetch } from "./apiClient";

/**
 * Fire-and-forget background generation of activities for each day.
 * Writes activities + aiInitialPassComplete per-day to Firestore.
 * On per-day failure: logs structured error, marks aiInitialPassComplete: false, continues.
 * Returns void — called without await.
 */
export async function generateTripActivities(tripId, days, cityOverrides, proxyUrl, tripBrief, wave2MustSees) {
  let successCount = 0;
  const generatedTitles = []; // accumulates titles across all days for cross-day dedup
  for (let i = 0; i < days.length; i++) {
    const day = days[i];
    const dayNumber = day.order || day.dayNumber || i + 1;
    const dayId = `${tripId}-day-${dayNumber}`;
    const effectiveCity = (cityOverrides && cityOverrides[dayId]) || day.city;

    // Build completed context from previous days with actual generated titles
    const completedContext = days.slice(0, i).map((d, idx) => ({
      dayNumber: d.order || d.dayNumber,
      city: (cityOverrides && cityOverrides[`${tripId}-day-${d.order || d.dayNumber}`]) || d.city,
      activities: generatedTitles
        .filter(t => t.dayIndex === idx)
        .map(t => ({ title: t.title, category: t.category })),
    }));

    const targetDay = {
      dayNumber,
      city: effectiveCity,
      country: day.country,
      date: day.date || day.displayDate,
      transitMode: day.transitMode || null,
      isTransitDay: day.isTransitDay || false,
    };

    let rawResponse = null;
    try {
      const res = await authenticatedFetch(`${proxyUrl}/api/generate-day-activities`, {
        method: "POST",
        body: JSON.stringify({
          tripBrief: tripBrief || {},
          wave1Days: days.map(d => ({
            dayNumber: d.order || d.dayNumber,
            city: (cityOverrides && cityOverrides[`${tripId}-day-${d.order || d.dayNumber}`]) || d.city,
            country: d.country,
            transitMode: d.transitMode || null,
            isTransitDay: d.isTransitDay || false,
          })),
          wave2MustSees: wave2MustSees || [],
          completedDays: completedContext,
          targetDay,
          language: getSetting('wanderologie_settings.language') || 'English',
          model: getSetting('wanderologie_settings.aiModel'),
        }),
      });

      if (res.status === 429) {
        console.error('[Wave3] Usage limit reached — stopping generation');
        break; // stop generating remaining days
      }

      try {
        rawResponse = await res.text();
      } catch (_) { /* ignore */ }

      let data;
      try {
        data = JSON.parse(rawResponse);
      } catch (parseErr) {
        throw new Error(`Invalid JSON from server: ${parseErr.message}`);
      }
      if (!res.ok) {
        throw new Error(data.error?.message || `Server error (${res.status})`);
      }

      const activities = data.activities || [];

      // Accumulate titles from this day's response for cross-day dedup
      activities.forEach(a => {
        if (a.title) {
          generatedTitles.push({ title: a.title, category: a.category, dayIndex: i });
        }
      });

      const dayMustSees = (wave2MustSees || []).filter(ms => ms.dayNumber === dayNumber);

      const batch = writeBatch(db);
      let order = 0;

      activities.forEach((activity) => {
        order++;
        const actId = `${tripId}-day-${dayNumber}-act-${order}`;
        const existingMustSee = dayMustSees.find(
          ms => ms.title && ms.title.toLowerCase() === (activity.title || "").toLowerCase()
        );

        if (existingMustSee && existingMustSee.id) {
          batch.update(doc(db, "activities", existingMustSee.id), {
            order: order,
            timeBlock: activity.timeBlock || null,
          });
        } else {
          const actDoc = stripUndefined({
            id: actId,
            tripId: tripId,
            ownerUid: auth.currentUser.uid,
            dayId: dayId,
            planType: "primary",
            order: order,
            aiGenerated: true,
            locked: false,
            confirmed: false,
            version: 1,
            title: activity.title,
            detail: activity.detail || null,
            category: VALID_CATEGORIES.includes(activity.category) ? activity.category : "leisure",
            timeBlock: activity.timeBlock || null,
            cost: activity.cost ?? null,
            costRange: getCostRange(activity.cost),
            duration: activity.duration || null,
            bookingStatus: VALID_BOOKING_STATUSES.includes(activity.bookingStatus) ? activity.bookingStatus : "not_needed",
            bookingLeadDays: activity.bookingLeadDays ?? null,
            aiRationale: activity.aiRationale || null,
            img: activity.img || null,
            imgCropParams: null,
            notes: null,
            alternative: null,
            bookingConfirmationRef: null,
            previousVersion: null,
            previousBookingStatus: null,
            location: activity.location || null,
            bookingUrl: activity.bookingUrl || null,
            bookingNote: activity.bookingNote || null,
            costDetail: activity.costDetail || null,
          });
          batch.set(doc(db, "activities", actId), actDoc);
        }
      });

      batch.update(doc(db, "days", dayId), {
        aiInitialPassComplete: true,
        ...(data.dayTheme ? { theme: data.dayTheme } : {}),
        ...(data.dayTip ? { tip: data.dayTip } : {}),
      });
      await batch.commit();
      successCount++;
    } catch (error) {
      console.error('[Wave3] Day generation failed', {
        dayId,
        dayCity: effectiveCity,
        errorMessage: error.message,
        errorStack: error.stack,
        rawResponsePreview: rawResponse ? rawResponse.slice(0, 500) : null,
      });
      // Mark failed day explicitly
      try {
        const failBatch = writeBatch(db);
        failBatch.update(doc(db, "days", dayId), { aiInitialPassComplete: false });
        await failBatch.commit();
      } catch (_) { /* best effort */ }
      // Continue to next day — do not throw
    }
  }

  // After all days processed, set trip status to ready (with flag if all failed)
  // Retry once after 2s on failure — self-heal in ItineraryView is the ultimate safety net
  const commitStatusReady = async () => {
    const finalBatch = writeBatch(db);
    finalBatch.update(doc(db, "trips", tripId), {
      status: "ready",
      ...(successCount === 0 ? { generationAllFailed: true } : {}),
    });
    await finalBatch.commit();
  };
  try {
    await commitStatusReady();
    if (successCount === 0) {
      console.error('[Wave3] All days failed generation — trip marked ready with generationAllFailed flag');
    }
  } catch (e) {
    console.error('[Wave3] Failed to set trip status to ready, retrying in 2s:', e);
    try {
      await new Promise(r => setTimeout(r, 2000));
      await commitStatusReady();
    } catch (retryErr) {
      console.error('[Wave3] Retry also failed to set trip status to ready:', retryErr);
    }
  }
}
